import React, { useContext, useMemo, useState } from 'react'
import copy from 'copy-to-clipboard'
import { useHistory, useRouteMatch } from 'react-router'
import qs from 'qs'

import {
  Dropdown,
  useDropdownState,
  ModalContext,
  Flex,
} from '@sketch/components'
import { useToast } from '@sketch/toasts'
import {
  QueryParams,
  versionedRoutes,
  routes,
  LaunchSketchAlert,
  useFlag,
  useUserSignedIn,
} from '@sketch/modules-common'
import { IS_EMBEDDED } from '@sketch/constants'
import { useVersioning } from '../../../../versioning'
import { PartialShareModalCreate } from '../../../modals/PartialShareModal'

import { StyledCanvasContextMenu, AppIcon } from './CanvasContextMenu.styles'
import { ContextMenuMetaData, Coordinates } from './useCanvasContextMenu'
import { INSPECTOR_SEGMENT } from '../../../constants'
import {
  useAnnotationOverlayContext,
  useCreateDraftAnnotation,
} from '../../../../annotations/hooks'

import {
  useShareSidebarTab,
  getActivePanels,
} from '../../../components/ShareSidebarTabContext'
import { usePreview } from '../../../views/PreviewView/PreviewContext'

type CanvasContextMenuProps = {
  permanentPageId: string
  contextMenuPos: Coordinates | null
  contextMenuMetaData: ContextMenuMetaData | null
  closeTooltip: () => void
}

/**
 * Menu displayed when right clicking on the canvas.
 */
function CanvasContextMenu({
  contextMenuPos,
  contextMenuMetaData,
  closeTooltip,
  permanentPageId,
}: CanvasContextMenuProps) {
  const { versionShortId, getPathname, share } = useVersioning()
  const previewContext = usePreview()
  const { showToast } = useToast()
  const { showModal } = useContext(ModalContext)
  const { path } = useRouteMatch()
  const history = useHistory()
  const isPartialSharingEnabled = useFlag('partial-sharing')
  const isSignedIn = useUserSignedIn()

  const userCanInspect = share
    ? share.userCanInspect
    : !!previewContext?.preview.inspectEnabled

  const activePanels = useMemo(
    () =>
      getActivePanels({
        path,
        canInspect: userCanInspect,
        isSignedIn,
      }),
    [path, userCanInspect, isSignedIn]
  )

  const [segments] = useState(activePanels)
  const { toggleSegment } = useShareSidebarTab(segments)

  const annotationsContext = useAnnotationOverlayContext()
  const handleDraftAnnotationEvent = useCreateDraftAnnotation(
    share?.identifier || ''
  )

  const dropdown = useDropdownState({
    onToggle: closeTooltip,
  })

  if (contextMenuPos === null || !contextMenuMetaData) {
    return null
  }

  function handleInpsectCanvas() {
    toggleSegment('Inspect')
    closeTooltip()
  }

  function handleCopyLinkToCoordinates() {
    if (!share) {
      return null
    }

    // We want to always use a versioned route (rather than implicitly linking
    // to the latest version) so that URLs are not affected when a new version
    // is created.
    let currentPathname
    if (permanentPageId) {
      // Only include a pageUUID if the user has navigated away from the first page
      currentPathname = versionedRoutes.SHARE_PAGE_CANVAS_VIEW.VERSION.create({
        pageUUID: permanentPageId,
        shareID: share.identifier,
        versionShortId,
      })
    } else {
      currentPathname = versionedRoutes.SHARE_VIEW.VERSION.create({
        shareID: share.identifier,
        versionShortId,
      })
    }

    const queryParams: QueryParams<'SHARE_PAGE_CANVAS_VIEW'> = {
      posX: contextMenuMetaData!.canvasPanPosition.posX.toString(),
      posY: contextMenuMetaData!.canvasPanPosition.posY.toString(),
      zoom: contextMenuMetaData!.zoom.toString(),
    }

    const link = `${window.location.origin}${currentPathname}?${qs.stringify(
      queryParams
    )}`
    copy(link)
    showToast('Link copied')
    closeTooltip()
  }

  function handleCopyLinkToPreview() {
    if (!previewContext) {
      return
    }

    const currentPathname = routes.PREVIEW_VIEW.create({
      previewID: previewContext.preview.identifier,
    })

    const link = `${window.location.origin}${currentPathname}`
    copy(link)
    showToast('Link copied')
    closeTooltip()
  }

  function handleCopyLinkToFrame() {
    if (!contextMenuMetaData?.artboardUUID || !share) {
      return
    }

    const currentPathname = getPathname({
      routeKey: 'FRAME',
      routeParams: {
        shareID: share.identifier,
        frameUUID: contextMenuMetaData.artboardUUID,
      },
    })

    const link = `${window.location.origin}${currentPathname}`
    copy(link)
    showToast('Link copied')
    closeTooltip()
  }

  function handleEdit() {
    if (!share) {
      return
    }

    closeTooltip()
    showModal(LaunchSketchAlert, {
      shareId: share.identifier,
      pageUUID: permanentPageId,
      action: 'openInSketch',
    })
  }

  function handleInspectFrame() {
    if (!contextMenuMetaData?.artboardUUID || !share) {
      return
    }

    const currentPathname = getPathname({
      routeKey: 'FRAME',
      routeParams: {
        shareID: share.identifier,
        frameUUID: contextMenuMetaData.artboardUUID,
      },
    })

    closeTooltip()
    history.push(`${currentPathname}#${INSPECTOR_SEGMENT}`)
  }

  const handleAddAnnotation = () => {
    if (handleDraftAnnotationEvent) {
      handleDraftAnnotationEvent(
        {
          clientX: contextMenuPos.posX,
          clientY: contextMenuPos.posY,
        },
        true
      )
    }
    closeTooltip()
  }

  const hasFrameUUID = contextMenuMetaData.artboardUUID !== null

  const dropdownTitle = hasFrameUUID ? 'Frame' : 'Canvas'
  const showViewFrame = userCanInspect && hasFrameUUID && !IS_EMBEDDED
  const showInspect = userCanInspect && !IS_EMBEDDED
  const showCreatePreview =
    isPartialSharingEnabled && share && share.userCanUpdateSettings

  if (previewContext) {
    return (
      <StyledCanvasContextMenu
        {...dropdown}
        placement="bottom-start"
        spacing="0px"
        style={{
          left: contextMenuPos.posX,
          top: contextMenuPos.posY,
        }}
        visible
        onClickOutside={closeTooltip}
      >
        <Dropdown.Header>Preview</Dropdown.Header>

        <Dropdown.Item onClick={handleCopyLinkToPreview}>
          Copy Link
        </Dropdown.Item>
      </StyledCanvasContextMenu>
    )
  }

  return (
    <StyledCanvasContextMenu
      {...dropdown}
      placement="bottom-start"
      spacing="0px"
      style={{
        left: contextMenuPos.posX,
        top: contextMenuPos.posY,
      }}
      visible={true}
      onClickOutside={closeTooltip}
    >
      <Dropdown.Header>{dropdownTitle}</Dropdown.Header>

      {contextMenuMetaData.artboardUUID !== null && (
        <Dropdown.Item onClick={handleCopyLinkToFrame}>Copy Link</Dropdown.Item>
      )}

      {share ? (
        <Dropdown.Item onClick={handleCopyLinkToCoordinates}>
          Copy Link from Position
        </Dropdown.Item>
      ) : null}

      {showViewFrame && (
        <Dropdown.Item onClick={handleInspectFrame}>View Frame</Dropdown.Item>
      )}

      {share && (share.commentsEnabled || share.userCanEdit) && (
        <Dropdown.Divider />
      )}

      {share &&
        share.commentsEnabled &&
        annotationsContext?.isViewingLatestVersion && (
          <Dropdown.Item onClick={handleAddAnnotation}>
            Add Comment…
          </Dropdown.Item>
        )}

      {showInspect && (
        <Dropdown.Item onClick={handleInpsectCanvas}>Inspect</Dropdown.Item>
      )}

      {share && share.userCanEdit && (
        <Dropdown.Item onClick={handleEdit}>
          <Flex alignItems="center">
            Edit in <AppIcon alt="Sketch" />
          </Flex>
        </Dropdown.Item>
      )}

      {share && showCreatePreview && (
        <>
          <Dropdown.Divider />
          <Dropdown.Item
            onClick={() => {
              showModal(PartialShareModalCreate, {
                shareName: share.name,
                shareIdentifier: share.identifier,
                versionShortId,
              })
              closeTooltip()
            }}
          >
            Share a Preview of this Page…
          </Dropdown.Item>
        </>
      )}
    </StyledCanvasContextMenu>
  )
}

export default CanvasContextMenu
